<template>
  <Content class="goodsBox">
    <Form id="searchForm" :model="formItem" :label-width="80" inline>
      <FormItem label="关键字">
        <Input
          v-model="formItem.key"
          placeholder="商品、订单号、用户昵称/手机"
          clearable
          style="width: 260px;"
        />
      </FormItem>
      <FormItem label="订单状态">
        <Select
          v-model="formItem.deliveryStatus"
          clearable
          placeholder="全部"
          style="width: 160px;"
        >
          <Option v-for="item in statusFilterOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </Option>
        </Select>
      </FormItem>
      <FormItem :label-width="20">
        <Button type="primary" @click="search">查询</Button>
        <Button style="margin-left: 8px" @click="clear">重置</Button>
      </FormItem>
    </Form>
    <Table
      :columns="columns"
      :data="tableData"
      :height="tablesHeight"
      border
    ></Table>
    <Page
      class="table_page_css"
      v-show="total"
      :total="total"
      :current="searchInfo.pageNo"
      :page-size="searchInfo.pageSize"
      :page-size-opts="pageSizeOpts"
      @on-change="pageNoChange"
      @on-page-size-change="pageSizeChange"
      show-sizer
      show-elevator
      show-total
    />
  </Content>
</template>
<script>
import {
  getfoodOrderList,
  adminConfirmFoodOrder,
  adminPickingDoneFoodOrder,
  updateordertype,
} from '@/api/foodorder'

const DELIVERY_STATUS_MAP = {
  PENDING_CONFIRM: '待确认',
  PREPARING: '备货中',
  WAIT_DISPATCH: '待分配',
  DISPATCHING: '派送中',
  WAIT_PICKUP: '待取货',
  RETURNING_DEPOT: '返仓中',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
  WAIT_MERCHANT: '待确认',
  CONFIRMED: '备货中',
  READY_ASSIGN: '待分配',
  EN_ROUTE: '派送中',
  ASSIGNED: '派送中',
  ARRIVED: '待取货',
}

function formatItemLines(row) {
  const oi = row && row.order_items
  if (Array.isArray(oi) && oi.length > 0) {
    return oi
      .filter((it) => it && typeof it === 'object')
      .map((it) => {
        const name =
          (it.food_name != null && String(it.food_name).trim() !== ''
            ? it.food_name
            : it.name) || '商品'
        const q = Number(it.quantity)
        const qty = Number.isFinite(q) && q > 0 ? Math.floor(q) : 1
        return { name: String(name).trim(), qty }
      })
  }
  const title =
    (row.food_name && String(row.food_name).trim()) ||
    (row.foodDetails && row.foodDetails.name) ||
    '商品'
  const qn = row && row.num != null ? Number(row.num) : NaN
  const qty = Number.isFinite(qn) && qn > 0 ? Math.floor(qn) : 1
  return [{ name: String(title), qty }]
}

function formatProductSummary(row) {
  const lines = formatItemLines(row)
  if (!lines.length) return '—'
  return lines.map((l) => `${l.name} ×${l.qty}`).join('、')
}

function formatPayAmount(row) {
  const wx = row && row.wx_total_fen
  if (wx != null && !Number.isNaN(Number(wx)) && Number(wx) > 0) {
    return (Number(wx) / 100).toFixed(2)
  }
  const pm = row && row.pay_mes
  if (pm && pm.amount != null && !Number.isNaN(Number(pm.amount))) {
    return (Number(pm.amount) / 100).toFixed(2)
  }
  return '—'
}

function shortId(id) {
  if (id == null || id === '') return '—'
  const s = String(id)
  if (s.length <= 14) return s
  return `${s.slice(0, 6)}…${s.slice(-6)}`
}

function orderStatusText(row) {
  if (row && row.status_label) return row.status_label
  const ds = row && row.delivery_status
  if (ds) return DELIVERY_STATUS_MAP[ds] || ds
  const typ = Number(row && row.type)
  if (typ === 1) return '已下单'
  if (typ === 2) return '运输中'
  if (typ === 3) return '已送达'
  return '—'
}

function isDineInOrder(row) {
  return row && String(row.fulfillment_mode || '').trim() === 'dine_in'
}

function formatFulfillmentMode(row) {
  if (isDineInOrder(row)) {
    const no = row.table_no != null ? String(row.table_no).trim() : ''
    return no ? { label: '堂食', sub: `${no} 号桌` } : { label: '堂食', sub: '' }
  }
  return { label: '外卖', sub: '' }
}

function deliveryTaskText(row) {
  if (isDineInOrder(row)) return '—'
  const brief = row && row.delivery_task_brief
  if (!brief || !brief._id) {
    const ds = row && row.delivery_status
    if (ds === 'WAIT_DISPATCH' || ds === 'PREPARING') return '待派车'
    return '—'
  }
  const parts = [brief.status_label || brief.status || '配送中']
  if (brief.car_ref) parts.push(`车辆 ${brief.car_ref}`)
  return parts.join(' · ')
}

function getOrderActions(row) {
  const cr =
    row.delivery_cancel_request && row.delivery_cancel_request.status
  if (cr === 'pending' || cr === 'processing') {
    return [{ key: 'cancel_pending', label: '取消待处理', type: 'default', disabled: true }]
  }
  const ds = row.delivery_status
  if (ds === 'PENDING_CONFIRM') {
    return [{ key: 'confirm', label: '确认订单', type: 'primary', confirm: '确认接受该订单？' }]
  }
  if (ds === 'PREPARING') {
    return [
      {
        key: 'picking_done',
        label: isDineInOrder(row) ? '出餐完成' : '备货完成',
        type: 'primary',
        confirm: isDineInOrder(row)
          ? '确认出餐完成？'
          : '确认备货完成，进入待派车？',
      },
    ]
  }
  if (ds === 'WAIT_DISPATCH') {
    return [{ key: 'wait_dispatch', label: '待派车', type: 'default', disabled: true }]
  }
  if (
    ds === 'DISPATCHING' ||
    ds === 'WAIT_PICKUP' ||
    ds === 'RETURNING_DEPOT'
  ) {
    return [{ key: 'in_delivery', label: '配送中', type: 'default', disabled: true }]
  }
  if (ds === 'COMPLETED' || ds === 'CANCELLED') {
    return []
  }
  const typ = Number(row.type)
  if (!ds && typ === 1) {
    return [
      {
        key: 'legacy_ship',
        label: '发货',
        type: 'warning',
        confirm: '将该订单标记为运输中？',
      },
    ]
  }
  if (!ds && typ === 2) {
    return [
      {
        key: 'legacy_deliver',
        label: '送达',
        type: 'success',
        confirm: '确认订单已送达？',
      },
    ]
  }
  return []
}

export default {
  name: 'FoodOrder',

  data() {
    return {
      statusFilterOptions: [
        { value: 'PENDING_CONFIRM', label: '待确认' },
        { value: 'PREPARING', label: '备货中' },
        { value: 'WAIT_DISPATCH', label: '待分配' },
        { value: 'DISPATCHING', label: '派送中' },
        { value: 'WAIT_PICKUP', label: '待取货' },
        { value: 'RETURNING_DEPOT', label: '返仓中' },
        { value: 'COMPLETED', label: '已完成' },
        { value: 'CANCELLED', label: '已取消' },
      ],
      columns: [
        { type: 'index', width: 56, align: 'center' },
        {
          title: '下单用户',
          key: 'customer',
          minWidth: 110,
          render: (h, { row }) => {
            const c = row.customer_brief
            if (!c) return h('span', '—')
            const name = c.nickname || '—'
            const phone = c.phone ? `（${c.phone}）` : ''
            return h('div', [h('div', name), phone ? h('div', { style: { color: '#909399', fontSize: '12px' } }, phone) : null])
          },
        },
        {
          title: '下单时间',
          key: 'createtime',
          width: 150,
          render: (h, { row }) => h('span', row.createtime || row.pay_time || '—'),
        },
        {
          title: '用餐方式',
          key: 'fulfillment_mode',
          width: 100,
          align: 'center',
          render: (h, { row }) => {
            const fm = formatFulfillmentMode(row)
            const isDine = fm.label === '堂食'
            const children = [
              h(
                'Tag',
                { props: { color: isDine ? 'warning' : 'blue' } },
                fm.label
              ),
            ]
            if (fm.sub) {
              children.push(
                h(
                  'div',
                  { style: { color: '#909399', fontSize: '12px', marginTop: '4px' } },
                  fm.sub
                )
              )
            }
            return h('div', children)
          },
        },
        {
          title: '商品 / 数量',
          key: 'products',
          minWidth: 180,
          render: (h, { row }) => {
            const children = [h('div', formatProductSummary(row))]
            if (row.bundle_note) {
              children.push(
                h(
                  'div',
                  { style: { color: '#909399', fontSize: '12px', marginTop: '4px' } },
                  row.bundle_note
                )
              )
            }
            return h('div', children)
          },
        },
        {
          title: '订单号',
          key: 'orderNo',
          width: 130,
          render: (h, { row }) => {
            const no =
              (row.pay_mes && row.pay_mes.outTradeNo) ||
              row.wx_out_trade_no ||
              shortId(row._id)
            return h('span', { attrs: { title: String(row._id || '') } }, no)
          },
        },
        {
          title: '金额(元)',
          key: 'amount',
          width: 90,
          align: 'right',
          render: (h, { row }) => h('span', formatPayAmount(row)),
        },
        {
          title: '订单状态',
          key: 'status',
          width: 100,
          render: (h, { row }) => {
            const text = orderStatusText(row)
            const cancelPending =
              row.delivery_cancel_request &&
              (row.delivery_cancel_request.status === 'pending' ||
                row.delivery_cancel_request.status === 'processing')
            const color = cancelPending
              ? 'warning'
              : row.delivery_status === 'CANCELLED'
              ? 'default'
              : row.delivery_status === 'COMPLETED' || Number(row.type) === 3
              ? 'success'
              : 'primary'
            return h('Tag', { props: { color } }, text)
          },
        },
        {
          title: '配送任务',
          key: 'delivery_task',
          minWidth: 120,
          render: (h, { row }) => h('span', deliveryTaskText(row)),
        },
        {
          title: '操作',
          key: 'action',
          width: 130,
          align: 'center',
          fixed: 'right',
          render: (h, { row }) => {
            const actions = getOrderActions(row)
            if (!actions.length) return h('span', { style: { color: '#c5c8ce' } }, '—')
            return h(
              'div',
              actions.map((act) => {
                const btn = h(
                  'Button',
                  {
                    props: {
                      type: act.type,
                      size: 'small',
                      disabled: act.disabled,
                    },
                    style: { marginRight: '6px', marginBottom: '4px' },
                    on: act.disabled || act.confirm ? {} : { click: () => this.handleAction(act.key, row) },
                  },
                  act.label
                )
                if (act.confirm && !act.disabled) {
                  return h(
                    'Poptip',
                    {
                      props: {
                        confirm: true,
                        transfer: true,
                        title: act.confirm,
                      },
                      on: {
                        'on-ok': () => this.handleAction(act.key, row),
                      },
                    },
                    [btn]
                  )
                }
                return btn
              })
            )
          },
        },
      ],
      tableData: [],
      total: 0,
      searchInfo: {
        pageNo: 1,
        pageSize: 10,
        key: '',
        deliveryStatus: '',
      },
      pageSizeOpts: [10, 20, 50, 100],
      formItem: {
        key: '',
        deliveryStatus: '',
      },
      tablesHeight: 0,
    }
  },
  methods: {
    setTablesHeight() {
      const oA = document.getElementById('app')
      const sA = document.getElementById('searchForm')
      const oH = oA ? oA.clientHeight || oA.offsetHeight : 600
      const sH = sA ? sA.clientHeight || sA.offsetHeight : 80
      this.tablesHeight = Math.max(320, oH - sH - 265)
    },

    getList() {
      const payload = { ...this.searchInfo }
      if (!payload.key) delete payload.key
      if (!payload.deliveryStatus) {
        delete payload.deliveryStatus
      } else {
        payload.delivery_status = payload.deliveryStatus
        delete payload.deliveryStatus
      }
      getfoodOrderList(payload)
        .then((res) => {
          if (res.data.code === 200) {
            this.tableData = res.data.data || []
            this.total = res.data.total || 0
          } else {
            this.$Message.error(res.data.msg || '获取订单列表失败')
          }
        })
        .catch((err) => {
          this.$Message.error(err && err.message ? err.message : '获取订单列表失败')
        })
    },

    search() {
      this.searchInfo.pageNo = 1
      this.searchInfo.key = this.formItem.key
      this.searchInfo.deliveryStatus = this.formItem.deliveryStatus
      this.getList()
    },

    clear() {
      this.formItem = { key: '', deliveryStatus: '' }
      this.searchInfo = {
        pageNo: 1,
        pageSize: 10,
        key: '',
        deliveryStatus: '',
      }
      this.getList()
    },

    handleAction(key, row) {
      if (key === 'confirm') {
        this.runAdminAction(adminConfirmFoodOrder, row._id, '确认成功')
        return
      }
      if (key === 'picking_done') {
        this.runAdminAction(adminPickingDoneFoodOrder, row._id, '操作成功')
        return
      }
      if (key === 'legacy_ship') {
        this.runLegacyType(row._id, 2)
        return
      }
      if (key === 'legacy_deliver') {
        this.runLegacyType(row._id, 3)
      }
    },

    runLegacyType(_id, type) {
      updateordertype({ _id, type })
        .then((res) => {
          if (res.data.code === 200) {
            this.$Message.success('状态已更新')
            this.getList()
          } else {
            this.$Message.error(res.data.msg || '操作失败')
          }
        })
        .catch((err) => {
          this.$Message.error(err && err.message ? err.message : '操作失败')
        })
    },

    runAdminAction(apiFn, _id, okMsg) {
      apiFn({ _id })
        .then((res) => {
          if (res.data.code === 200) {
            this.$Message.success(okMsg)
            this.getList()
          } else {
            this.$Message.error(res.data.msg || '操作失败')
          }
        })
        .catch((err) => {
          this.$Message.error(err && err.message ? err.message : '操作失败')
        })
    },

    pageNoChange(current) {
      this.searchInfo.pageNo = current
      this.getList()
    },

    pageSizeChange(val) {
      this.searchInfo.pageNo = 1
      this.searchInfo.pageSize = val
      this.getList()
    },
  },
  mounted() {
    this.getList()
    setTimeout(() => this.setTablesHeight(), 50)
    window.onresize = () => this.setTablesHeight()
  },
}
</script>
<style lang="less" scoped>
.goodsBox {
  .ivu-form-inline .ivu-form-item {
    width: auto;
    min-width: 200px;
  }
}
</style>
